import { useState, useEffect } from "react";

/**
 * Custom hook for persisting tab selection across page refreshes and component re-renders
 * @param {string} storageKey - Unique key for localStorage
 * @param {number} defaultTabIndex - Default tab index to select if no saved value exists
 * @param {boolean} persistToStorage - Whether to persist to localStorage (default: true)
 * @returns {Object} - Object containing selectedTabIndex and setSelectedTabIndex
 */
export const useTabPersistence = (
  storageKey,
  defaultTabIndex = 0,
  persistToStorage = true
) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(() => {
    if (!persistToStorage) return defaultTabIndex;

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) {
        const parsed = parseInt(saved, 10);
        return isNaN(parsed) ? defaultTabIndex : parsed;
      }
    } catch (error) {
      console.warn("Failed to retrieve tab index from localStorage:", error);
    }
    return defaultTabIndex;
  });

  const handleTabChange = (newTabIndex) => {
    setSelectedTabIndex(newTabIndex);

    if (persistToStorage) {
      try {
        localStorage.setItem(storageKey, newTabIndex.toString());
      } catch (error) {
        console.warn("Failed to save tab index to localStorage:", error);
      }
    }
  };

  // Update localStorage when selectedTabIndex changes externally
  useEffect(() => {
    if (persistToStorage && selectedTabIndex !== null) {
      try {
        localStorage.setItem(storageKey, selectedTabIndex.toString());
      } catch (error) {
        console.warn("Failed to save tab index to localStorage:", error);
      }
    }
  }, [selectedTabIndex, storageKey, persistToStorage]);

  return {
    selectedTabIndex,
    setSelectedTabIndex: handleTabChange,
  };
};

export default useTabPersistence;
